import { t } from 'i18next';

import { ContextMenuItem } from '../../../../../../react/components/skyview/context-menu';
import { RevertChange } from '../../../../../elements/design/design-node/design-node';
import { FaceId } from '../../../../../elements/design/design-node/utils/face-node';
import {
  getLineSegmentFaces,
  LineSegmentId,
} from '../../../../../elements/design/design-node/utils/line-segment-node';
import { DesignTool } from '../../../../design-tool';
import { addAndCommitAction } from '../utils/actions';
import { deleteSelection } from '../utils/delete-selection';
import { deselect } from '../utils/select';

export const deleteSelectedGeometry = (tool: DesignTool): ContextMenuItem => {
  const selectedPoints = new Set([...tool.currentSelection.points]);
  const selectedLines = new Set([...tool.currentSelection.lineSegments]);
  const selectedFaces = new Set([...tool.currentSelection.faces]);

  adjustSelectionForDeletion(tool, selectedLines, selectedFaces);

  const disabled = selectedPoints.size + selectedFaces.size + selectedLines.size === 0;

  return {
    title: t('contextMenu.deleteSelected', { ns: 'skyviewDesign' }),
    action: () => {
      let changes: RevertChange[] = [];

      if (disabled) {
        return;
      }

      addAndCommitAction(tool, {
        commit: () => {
          changes = deleteSelection(tool.asset.designNode, {
            faces: [...selectedFaces.values()],
            lineSegments: [...selectedLines.values()],
            points: [...selectedPoints.values()],
          });
          tool.asset.designNode.refresh();
          tool.asset.refreshMetadata();
        },
        rollback: () => {
          tool.asset.designNode.revert(changes);
          tool.asset.designNode.refresh();
          tool.asset.refreshMetadata();
        },
      });
      deselect(tool);
      tool.contextMenuCallback?.();
    },
    disabled,
    hotkey: 'Delete',
  };
};

/**
 * Adjust the selection so that the expected geometry is deleted based on a few different criteria.
 */
function adjustSelectionForDeletion(
  tool: DesignTool,
  lineSegments: Set<LineSegmentId>,
  faces: Set<FaceId>,
) {
  const additionalFaces = new Set<FaceId>();
  lineSegments.forEach((lineSegmentId) => {
    const connectedFaces = getLineSegmentFaces(tool.asset.designNode, lineSegmentId);

    const deleteCount = connectedFaces.filter((faceId) => faces.has(faceId)).length;

    if (deleteCount === 0) {
      // Include surrounding triangles if only the line segment is selected.
      connectedFaces.forEach((faceId) => additionalFaces.add(faceId));
    } else if (deleteCount > 0 && deleteCount < connectedFaces.length) {
      // Skip the line segment if we are only deleting some of the connected triangles.
      lineSegments.delete(lineSegmentId);
    }
  });

  // Append the new faces to the selection.
  additionalFaces.forEach((x) => faces.add(x));
}
